import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Mail, Send, RefreshCw, CheckCircle2, XCircle, Clock, Search, Eye,
  Settings, Activity, AlertTriangle, Loader2, Save, RotateCw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailCenter = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  // Settings
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Detail
  const [selectedLog, setSelectedLog] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  // Invoice emails (legacy)
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState("logs");

  const RETRYABLE_TYPES = ["invoice_send", "direct"];

  const handleRetry = async (log, e) => {
    if (e) e.stopPropagation();
    setRetryingId(log.id);
    try {
      await axios.post(`${API}/email/logs/${log.id}/retry`);
      toast.success(`Retry sent to ${log.to}`);
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retry failed");
    } finally {
      setRetryingId(null);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 50, skip: page * 50 };
      if (search) params.search = search;
      if (typeFilter) params.email_type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const [logsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/email/logs`, { params }),
        axios.get(`${API}/email/summary`),
      ]);
      setLogs(logsRes.data.logs || []);
      setStats(logsRes.data.stats || {});
      setTotal(logsRes.data.total || 0);
      setSummary(summaryRes.data.by_type || {});
    } catch {}
    finally { setLoading(false); }
  };

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const res = await axios.get(`${API}/email/settings`);
      setSettings(res.data);
    } catch {}
    finally { setSettingsLoading(false); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/email/settings`, settings);
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSavingSettings(false); }
  };

  const fetchInvoices = async () => {
    try {
      const res = await axios.get(`${API}/invoices`);
      setInvoices(res.data.invoices || []);
    } catch {}
  };

  useEffect(() => { fetchLogs(); }, [page, typeFilter, statusFilter]);
  useEffect(() => { const t = setTimeout(fetchLogs, 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { fetchInvoices(); }, []);

  const typeLabel = (t) => ({
    task_assigned: "Task Assigned",
    task_reminder: "Reminder",
    task_overdue: "Overdue",
    invoice_notification: "Invoice Lock",
    invoice_send: "Invoice Send",
    direct: "Direct",
  }[t] || t || "Unknown");

  const typeColor = (t) => ({
    task_assigned: "bg-emerald-100 text-emerald-700",
    task_reminder: "bg-amber-100 text-amber-700",
    task_overdue: "bg-red-100 text-red-700",
    invoice_notification: "bg-violet-100 text-violet-700",
    invoice_send: "bg-blue-100 text-blue-700",
    direct: "bg-slate-100 text-slate-700",
  }[t] || "bg-slate-100 text-slate-500");

  const typeRingColor = (t) => ({
    task_assigned: "ring-emerald-400/60",
    task_reminder: "ring-amber-400/60",
    task_overdue: "ring-red-400/60",
    invoice_notification: "ring-violet-400/60",
    invoice_send: "ring-blue-400/60",
    direct: "ring-slate-400/60",
  }[t] || "ring-slate-400/60");

  // Canonical ordering for chips — shows every known type even when count is 0
  const CHIP_TYPES = [
    "invoice_send",
    "invoice_notification",
    "task_assigned",
    "task_reminder",
    "task_overdue",
    "direct",
  ];

  return (
    <div className="p-6" data-testid="email-center-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Email Center</h1>
          <p className="text-sm text-slate-500 mt-0.5">All emails sent from the platform</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { setShowSettings(true); fetchSettings(); }} data-testid="email-settings-btn">
            <Settings className="w-4 h-4 mr-1" />Settings
          </Button>
          <Button variant="outline" size="sm" onClick={fetchLogs}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
            <div><p className="text-2xl font-bold text-slate-900">{stats.total_sent || 0}</p><p className="text-xs text-slate-500">Total Sent</p></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center"><XCircle className="w-5 h-5 text-red-600" /></div>
            <div><p className="text-2xl font-bold text-slate-900">{stats.total_failed || 0}</p><p className="text-xs text-slate-500">Failed</p></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Activity className="w-5 h-5 text-blue-600" /></div>
            <div><p className="text-2xl font-bold text-slate-900">{stats.today || 0}</p><p className="text-xs text-slate-500">Sent Today</p></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center"><Mail className="w-5 h-5 text-violet-600" /></div>
            <div><p className="text-2xl font-bold text-slate-900">{Object.keys(summary).length}</p><p className="text-xs text-slate-500">Email Types</p></div>
          </div>
        </div>
      </div>

      {/* Filter Chips — one per type with live counts */}
      <div className="bg-white rounded-xl border p-3 mb-4" data-testid="email-type-chips">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => { setTypeFilter(""); setPage(0); }}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] font-medium transition-all ${
              !typeFilter
                ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
            }`}
            data-testid="chip-all"
          >
            <span>All</span>
            <span className={`text-[10px] font-mono ${!typeFilter ? "text-white/70" : "text-slate-400"}`}>{total}</span>
          </button>
          {CHIP_TYPES.map((t) => {
            const counts = summary[t] || { sent: 0, failed: 0 };
            const totalForType = (counts.sent || 0) + (counts.failed || 0);
            const isActive = typeFilter === t;
            return (
              <button
                key={t}
                onClick={() => { setTypeFilter(isActive ? "" : t); setPage(0); }}
                className={`group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] font-medium transition-all ${
                  isActive
                    ? `${typeColor(t)} border-transparent shadow-sm ring-2 ${typeRingColor(t)}`
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                }`}
                data-testid={`chip-${t}`}
              >
                <span>{typeLabel(t)}</span>
                <span className={`text-[10px] font-mono ${isActive ? "opacity-70" : "text-slate-400"}`}>{totalForType}</span>
                {counts.failed > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[9px] text-red-600 font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    {counts.failed}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border p-3 mb-4 flex items-stretch sm:items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-0 basis-full sm:basis-auto sm:max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input className="h-10 sm:h-8 pl-8 text-[13px] sm:text-[12px] w-full" placeholder="Search email, subject, resource..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} />
        </div>
        <Select value={typeFilter || "all"} onValueChange={v => { setTypeFilter(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="h-10 sm:h-8 flex-1 sm:flex-none sm:w-36 text-[13px] sm:text-[12px]"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="invoice_send">Invoice Send</SelectItem>
            <SelectItem value="invoice_notification">Invoice Lock</SelectItem>
            <SelectItem value="task_assigned">Task Assigned</SelectItem>
            <SelectItem value="task_reminder">Reminder</SelectItem>
            <SelectItem value="task_overdue">Overdue</SelectItem>
            <SelectItem value="direct">Direct</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter || "all"} onValueChange={v => { setStatusFilter(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="h-10 sm:h-8 flex-1 sm:flex-none sm:w-28 text-[13px] sm:text-[12px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[11px] text-slate-400 basis-full sm:basis-auto sm:ml-auto">{total} emails</span>
      </div>

      {/* Email Logs Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>
        ) : (
          <div className="overflow-x-auto scroll-x-hint">
            <table className="w-full min-w-[640px]">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400 hidden sm:table-cell">Time</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">To</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400 hidden md:table-cell">Subject</th>
                  <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400 hidden sm:table-cell">Type</th>
                  <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Status</th>
                  <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400 hidden lg:table-cell">Resource</th>
                  <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400 w-[90px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} className="border-b hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setSelectedLog(log)}>
                    <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap hidden sm:table-cell">{new Date(log.sent_at).toLocaleString()}</td>
                    <td className="px-3 py-2 text-[12px] text-slate-700 max-w-[180px] truncate">
                      <div className="truncate">{log.to}</div>
                      <div className="sm:hidden text-[10px] text-slate-400 truncate">{log.subject}</div>
                    </td>
                    <td className="px-3 py-2 text-[12px] text-slate-700 max-w-[250px] truncate font-medium hidden md:table-cell">{log.subject}</td>
                    <td className="px-3 py-2 text-center hidden sm:table-cell"><Badge className={`text-[9px] border-0 ${typeColor(log.type)}`}>{typeLabel(log.type)}</Badge></td>
                    <td className="px-3 py-2 text-center">
                      {log.status === "sent" ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[9px] border-0"><CheckCircle2 className="w-3 h-3 mr-0.5" />Sent</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 text-[9px] border-0"><XCircle className="w-3 h-3 mr-0.5" />Failed</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center text-[11px] text-slate-400 hidden lg:table-cell">{log.resource_email || "-"}</td>
                    <td className="px-3 py-2 text-center">
                      {log.status === "failed" && RETRYABLE_TYPES.includes(log.type) && !log.retried ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-[10px] border-amber-200 text-amber-700 hover:bg-amber-50"
                          disabled={retryingId === log.id}
                          onClick={(e) => handleRetry(log, e)}
                          data-testid={`retry-email-${log.id}`}
                        >
                          {retryingId === log.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3 mr-0.5" />}
                          {retryingId === log.id ? "…" : "Retry"}
                        </Button>
                      ) : log.retried ? (
                        <span className="text-[10px] text-slate-400">Retried</span>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-12 text-sm text-slate-400">
                    <Mail className="w-8 h-8 mx-auto mb-2 text-slate-200" />No emails found
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 50 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-xs text-slate-500">Page {page + 1} of {Math.ceil(total / 50)}</span>
          <Button variant="outline" size="sm" disabled={(page + 1) * 50 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* Log Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-blue-600" />Email Detail</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-xs text-slate-400 uppercase">To</span><p className="font-medium text-slate-700">{selectedLog.to}</p></div>
                <div><span className="text-xs text-slate-400 uppercase">Status</span><p>{selectedLog.status === "sent" ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px] border-0">Sent</Badge> : <Badge className="bg-red-100 text-red-700 text-[10px] border-0">Failed</Badge>}</p></div>
                <div><span className="text-xs text-slate-400 uppercase">Type</span><p><Badge className={`text-[10px] border-0 ${typeColor(selectedLog.type)}`}>{typeLabel(selectedLog.type)}</Badge></p></div>
                <div><span className="text-xs text-slate-400 uppercase">Time</span><p className="text-xs text-slate-600">{new Date(selectedLog.sent_at).toLocaleString()}</p></div>
                <div className="col-span-2"><span className="text-xs text-slate-400 uppercase">Subject</span><p className="text-sm font-medium text-slate-800">{selectedLog.subject}</p></div>
                {selectedLog.resource_email && <div className="col-span-2"><span className="text-xs text-slate-400 uppercase">Resource (work)</span><p className="text-xs text-slate-600">{selectedLog.resource_email}</p></div>}
                {selectedLog.sender && <div className="col-span-2"><span className="text-xs text-slate-400 uppercase">From</span><p className="text-xs text-slate-600">{selectedLog.sender}</p></div>}
                {selectedLog.error && <div className="col-span-2"><span className="text-xs text-red-400 uppercase">Error</span><p className="text-xs text-red-600 bg-red-50 p-2 rounded">{selectedLog.error}</p></div>}
              </div>
              {selectedLog.status === "failed" && RETRYABLE_TYPES.includes(selectedLog.type) && !selectedLog.retried && (
                <Button
                  className="w-full mt-2"
                  variant="outline"
                  disabled={retryingId === selectedLog.id}
                  onClick={async () => { await handleRetry(selectedLog); setSelectedLog(null); }}
                  data-testid="dialog-retry-btn"
                >
                  {retryingId === selectedLog.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCw className="w-4 h-4 mr-1" />}
                  {retryingId === selectedLog.id ? "Retrying…" : "Retry This Email"}
                </Button>
              )}
              {selectedLog.retried && (
                <p className="mt-2 text-xs text-slate-400 italic text-center">Already retried on {selectedLog.retried_at ? new Date(selectedLog.retried_at).toLocaleString() : "an earlier date"}.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg" data-testid="email-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5 text-indigo-600" />Email Settings</DialogTitle>
            <DialogDescription>Configure email sender addresses and display names</DialogDescription>
          </DialogHeader>
          {settingsLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>
          ) : (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Task Sender Email</label>
                  <Input value={settings.task_sender_email || ""} disabled className="h-8 text-xs bg-slate-50" />
                  <p className="text-[9px] text-slate-400 mt-0.5">Set in .env as TASK_SENDER_EMAIL</p>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Invoice Sender Email</label>
                  <Input value={settings.invoice_sender_email || ""} disabled className="h-8 text-xs bg-slate-50" />
                  <p className="text-[9px] text-slate-400 mt-0.5">Set in .env as SENDER_EMAIL</p>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">Reply-To Email</label>
                <Input value={settings.reply_to_email || ""} onChange={e => setSettings(s => ({ ...s, reply_to_email: e.target.value }))} className="h-8 text-xs" placeholder="tasks@graphikpro.com" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">From Name (Tasks)</label>
                  <Input value={settings.from_name_tasks || ""} onChange={e => setSettings(s => ({ ...s, from_name_tasks: e.target.value }))} className="h-8 text-xs" placeholder="Graphikpro Tasks" />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase mb-1 block">From Name (Invoices)</label>
                  <Input value={settings.from_name_invoices || ""} onChange={e => setSettings(s => ({ ...s, from_name_invoices: e.target.value }))} className="h-8 text-xs" placeholder="Graphikpro Invoicing" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${settings.resend_configured ? "bg-emerald-500" : "bg-red-500"}`} />
                  <span className="text-xs text-slate-600">Resend (Email) {settings.resend_configured ? "Connected" : "Not configured"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${settings.twilio_configured ? "bg-emerald-500" : "bg-red-500"}`} />
                  <span className="text-xs text-slate-600">Twilio (WhatsApp) {settings.twilio_configured ? "Connected" : "Not configured"}</span>
                </div>
              </div>
              <Button className="w-full" onClick={saveSettings} disabled={savingSettings}>
                {savingSettings ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                {savingSettings ? "Saving..." : "Save Settings"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailCenter;
